import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { isNil } from 'lodash-es';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  switchMap,
  tap,
} from 'rxjs';
import { HasPermissionDirective, PermissionService } from '@ipg/ngx-frontend-toolset';
import { DialogService } from '../../components/dialog/dialog.service';
import { UnitService } from '../units/services/unit.service';

const invoiceFieldsValidator =
  (isRootUnit: () => boolean): ValidatorFn =>
  (control: AbstractControl): ValidationErrors | null => {
    const purchaseNumber = control.get('purchaseNumber');
    const invoiceEmail = control.get('invoiceEmail');

    if (!purchaseNumber || !invoiceEmail) {
      return null;
    }

    const hasPurchaseNumber = !!purchaseNumber.value?.trim();
    const hasInvoiceEmail = !!invoiceEmail.value?.trim();

    // Root units: both fields are always required
    if (isRootUnit()) {
      if (!hasPurchaseNumber) {
        purchaseNumber.setErrors({ requiredForRoot: true });
      } else if (purchaseNumber.hasError('requiredForRoot')) {
        purchaseNumber.setErrors(null);
      }
      if (!hasInvoiceEmail) {
        invoiceEmail.setErrors({ requiredForRoot: true });
      } else if (invoiceEmail.hasError('requiredForRoot')) {
        invoiceEmail.setErrors(null);
      }
      return null;
    }

    // Non-root units: both empty or both filled — clear any cross-field errors
    if (hasPurchaseNumber === hasInvoiceEmail) {
      if (purchaseNumber.hasError('requiredByPeer')) {
        purchaseNumber.setErrors(null);
      }
      if (invoiceEmail.hasError('requiredByPeer')) {
        invoiceEmail.setErrors(null);
      }
      return null;
    }

    // One filled, the other not — mark the empty one
    if (hasPurchaseNumber && !hasInvoiceEmail) {
      invoiceEmail.setErrors({ requiredByPeer: true });
    } else {
      purchaseNumber.setErrors({ requiredByPeer: true });
    }

    return null;
  };

@Component({
  selector: 'app-settings-billing',
  imports: [
    MatButton,
    MatFormField,
    MatIcon,
    MatInput,
    MatError,
    ReactiveFormsModule,
    HasPermissionDirective,
  ],
  templateUrl: './settings-billing.component.html',
  styleUrl: './settings-billing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsBillingComponent {
  private readonly _dialog = inject(DialogService);
  private readonly _snackbar = inject(MatSnackBar);
  private readonly _fb = inject(FormBuilder);
  private readonly _unitService = inject(UnitService);
  private readonly _permissionService = inject(PermissionService);

  protected readonly isRootUnit = signal(false);

  protected readonly form = this._fb.group({
    invoice: this._fb.group(
      {
        purchaseNumber: this._fb.control('', [Validators.maxLength(100)]),
        invoiceEmail: this._fb.control('', [Validators.email]),
      },
      { validators: invoiceFieldsValidator(() => this.isRootUnit()) }
    ),
  });

  protected readonly submitted = signal(false);

  get invoiceControls() {
    return this.form.controls.invoice.controls;
  }

  constructor() {
    this.form.controls.invoice.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.form.controls.invoice.updateValueAndValidity({ emitEvent: false });
      });

    toObservable(this._unitService.currentUnit)
      .pipe(
        takeUntilDestroyed(),
        filter((result) => !isNil(result)),
        tap((result) => {
          this.isRootUnit.set(result.prevUnitId === null);
          this.form.patchValue({
            invoice: {
              purchaseNumber: result.invoice?.purchaseNumber ?? '',
              invoiceEmail: result.invoice?.invoiceEmail ?? '',
            },
          });
        })
      )
      .subscribe();

    if (!this._permissionService.hasPermission('unit:update')) {
      this.form.disable();
    }
  }

  handleSubmit() {
    this.submitted.set(true);

    const currentUnit = this._unitService.currentUnit();

    if (!currentUnit) {
      return;
    }

    const data = this.form.value;

    // When both invoice fields are empty (non-root units),
    // send null so the backend doesn't try to validate empty strings.
    const invoice = data.invoice;
    const hasInvoiceData =
      !!invoice?.purchaseNumber?.trim() || !!invoice?.invoiceEmail?.trim();

    this._unitService
      .update(currentUnit._id, {
        ...data,
        invoice: hasInvoiceData ? data.invoice : null,
      })
      .pipe(
        catchError(() => {
          this._dialog.alert({
            title: 'Foutmelding',
            message:
              'Er is iets misgegaan bij het opslaan van de instellingen. Probeer het later nog eens',
            confirmText: 'OK',
          });

          return EMPTY;
        }),
        switchMap(() => this._unitService.getCurrent()),

        finalize(() => this.submitted.set(false))
      )
      .subscribe({
        next: () => this._snackbar.open('Instellingen opgeslagen'),
      });
  }
}
